import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { type ChatbotRequestContext, messageChatbot, type ApiConfig } from '@ev-tech/search-modules-api';

import { prepareRequest, acceptChatbotResponse, confirmSkill, declineSkill } from './chatbotRequestHelpers';
import type { ChatbotStore } from '../stores/useChatbot';

type MutationParams = {
  message: string;
  retry: boolean;
  controller: AbortController;
};

export const useChatbotRequest = ({
  apiConfig,
  context,
  store,
}: {
  apiConfig: ApiConfig;
  context?: ChatbotRequestContext;
  store: ChatbotStore;
}) => {
  const activeRequest = useRef<AbortController | null>(null);
  const activeConfirmation = useRef<AbortController | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const cancelRequest = useCallback(() => {
    activeRequest.current?.abort();
    activeRequest.current = null;
    activeConfirmation.current?.abort();
    activeConfirmation.current = null;
  }, []);

  // Ignore late request and callback results when the provider unmounts.
  useEffect(() => cancelRequest, [cancelRequest]);

  const { mutate, reset, isPending } = useMutation({
    mutationKey: ['ev-chatbot', store.persist.getOptions().name],
    retry: false,
    mutationFn: async ({ message, retry, controller }: MutationParams) => {
      const isCancelled = () => controller.signal.aborted;

      if (isCancelled()) return;

      try {
        const prepared = prepareRequest(store, { message, retry, context });

        if (!prepared) return;

        const result = await messageChatbot(prepared.request, {
          ...apiConfig,
          signal: controller.signal,
          onError: (error) => {
            if (!isCancelled()) apiConfig.onError?.(error);
          },
        });

        if (isCancelled()) return;
        if (result.status === 'error') throw result.error;

        await acceptChatbotResponse(store, { response: result.data, requestedSkills: prepared.requestedSkills, signal: controller.signal });
      } finally {
        // A cancelled request may finish after a new one has already started.
        if (activeRequest.current === controller) activeRequest.current = null;
      }
    },
  });

  const sendMessage = useCallback(
    (message: string, { retry = false }: { retry?: boolean } = {}) => {
      const trimmedMessage = message.trim();

      if (activeRequest.current || activeConfirmation.current || (!retry && !trimmedMessage)) return;

      const controller = new AbortController();

      activeRequest.current = controller;
      mutate({ message: trimmedMessage, retry, controller });
    },
    [mutate],
  );

  const confirmAction = useCallback(
    async (messageId: string) => {
      if (activeRequest.current || activeConfirmation.current) return;

      const controller = new AbortController();

      activeConfirmation.current = controller;
      setIsConfirming(true);

      try {
        await confirmSkill(store, { messageId, signal: controller.signal });
      } finally {
        if (activeConfirmation.current === controller) {
          activeConfirmation.current = null;
          setIsConfirming(false);
        }
      }
    },
    [store],
  );

  const declineAction = useCallback((messageId: string) => declineSkill(store, messageId), [store]);

  const newChat = useCallback(() => {
    cancelRequest();
    setIsConfirming(false);
    reset();
    store.getState().clearConversation();
  }, [cancelRequest, reset, store]);

  return { sendMessage, newChat, isPending, isConfirming, confirmAction, declineAction };
};
